// M15 (2026-04-23) — Component TOTP input 6-digit với auto-submit optional.
//
// Props:
//   value         — string 0-6 digits
//   onChange(v)   — caller update state
//   onSubmit?(v)  — optional: fire khi đủ 6 số (auto-submit)
//   autoFocus?    — default false
//   disabled?     — default false
//   placeholder?  — default "123456"
//   ariaLabel?    — default "Mã TOTP 6 số"
//   testId?       — optional data-testid
//
// Behavior:
//   - Chỉ nhận chữ số (strip mọi ký tự khác)
//   - Tối đa 6 ký tự
//   - Nếu có onSubmit + đủ 6 số → gọi onSubmit(value) ngay sau update state
//     (tránh gọi nhiều lần bằng ref track lần cuối đã fire)
//   - KHÔNG auto-submit cho hành động destructive (bulk delete) — caller tự
//     quyết bằng cách KHÔNG pass onSubmit.
//
// Style: mono, tracking-widest, center alignment cho dễ đọc.

import { useEffect, useRef } from 'react';

export default function TotpInput({
  value,
  onChange,
  onSubmit,
  autoFocus = false,
  disabled = false,
  placeholder = '123456',
  ariaLabel = 'Mã TOTP 6 số',
  testId,
  className = '',
}) {
  const inputRef = useRef(null);
  const lastFiredValue = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  function handleChange(e) {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    onChange(digits);

    // Auto-submit khi đủ 6 số và caller cho phép
    if (digits.length === 6 && typeof onSubmit === 'function' && !disabled) {
      // Chống fire trùng (React double-invoke trong strict mode, hoặc user paste 6 số rồi edit lại)
      if (lastFiredValue.current !== digits) {
        lastFiredValue.current = digits;
        // setTimeout 0 để state update xong rồi mới submit — tránh race với form parent
        setTimeout(() => {
          if (typeof onSubmit === 'function') onSubmit(digits);
        }, 0);
      }
    } else if (digits.length < 6) {
      // Reset tracker nếu user xoá bớt số — cho phép fire lần sau khi lại đủ 6
      lastFiredValue.current = null;
    }
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      aria-label={ariaLabel}
      maxLength={6}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={handleChange}
      data-testid={testId}
      className={
        `w-full border rounded px-3 py-2 text-lg font-mono tracking-[0.5em] text-center disabled:opacity-40 ${className}`
      }
    />
  );
}
